import { fetchData } from 'Utils/fetch-data';
import type { FetchResponseI } from 'types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type SessionStatus = 'pending' | 'in_progress' | 'completed';
export type AttendanceStatus = 'present' | 'absent' | 'justified';

export interface UserMini {
  id: string;
  fullName: string;
  payrollNum?: number | string;
}

export interface SessionCourseRef {
  id: string;
  name: string;
}

export interface SessionI {
  id: string;
  courseId: string;
  title: string;
  description: string | null;
  startDateTime: string; // ISO UTC
  endDateTime: string; // ISO UTC
  instructorUserId: string | null;
  instructorName: string | null;
  location: string | null;
  isManuallyCompleted: boolean;
  manuallyCompletedAt: string | null;
  manuallyCompletedBy: string | null;
  userCreatedId: string;
  userUpdatedId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  status: SessionStatus;
  course?: SessionCourseRef;
  instructor?: UserMini | null;
  notes?: string | null; // server-side feature flag — present only after backend FR-030 ships
}

export interface SessionStatsCountersI {
  present: number;
  justified: number;
  absent: number;
  pending: number;
}

/**
 * Per-session attendance KPIs returned by `GET /api/sessions/:id/stats`.
 *
 *  - `attendancePercentage` = round((present + justified) / totalExpected * 100)
 *  - When `totalExpected === 0`, the percentage is 0.
 *  - Soft-deleted attendances are excluded.
 */
export interface SessionStatsI {
  attendancePercentage: number;
  counters: SessionStatsCountersI;
  totalExpected: number;
}

export interface SessionDetailI extends SessionI {
  attendances: SessionAttendanceI[];
  evidences: SessionEvidenceI[];
}


export interface SessionAttendanceI {
  id: string;
  sessionId: string;
  userId: string;
  status: AttendanceStatus | null; // null = pendiente
  comment: string | null;
  markedByUserId: string | null;
  markedAt: string | null;
  lastUpdatedByUserId: string | null;
  lastUpdatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  user?: UserMini;
  markedBy?: UserMini | null;
}

export interface SessionEvidenceI {
  id: string;
  sessionId: string;
  fileUrl: string; // S3 key, not a public URL — call getSignedUrl()
  fileName: string;
  fileType: string | null;
  fileSize: number | null;
  uploadedByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  uploader?: UserMini;
}

export interface SessionFormPayload {
  courseId?: string; // required on create; omitted on update
  title?: string;
  description?: string | null;
  startDateTime?: string; // ISO UTC
  endDateTime?: string; // ISO UTC
  instructorUserId?: string | null;
  instructorName?: string | null;
  location?: string | null;
}

export interface SessionsListFiltersI {
  page?: number;
  limit?: number;
  courseId?: string;
  instructorUserId?: string;
  userId?: string;
  status?: SessionStatus;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface AttendanceListFiltersI {
  page?: number;
  limit?: number;
  status?: AttendanceStatus | 'pending';
  search?: string;
}

export interface AttendanceBulkItem {
  userId: string;
  status: AttendanceStatus;
  comment: string | null;
}

export interface AttendanceBulkPayload {
  attendances: AttendanceBulkItem[];
}

export interface EvidencesListFiltersI {
  page?: number;
  limit?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// API client
// ─────────────────────────────────────────────────────────────────────────────

class SessionsApi {
  private readonly endpoint = '/sessions';

  // ── Sessions (1–7) ─────────────────────────────────────────────────────────

  async list(filters: SessionsListFiltersI = {}): Promise<FetchResponseI<SessionI[]>> {
    return fetchData<SessionI[]>(this.endpoint, {
      method: 'GET',
      params: filters,
      includePagination: true,
    });
  }

  async getById(id: string): Promise<FetchResponseI<SessionDetailI>> {
    return fetchData<SessionDetailI>(`${this.endpoint}/${id}`, { method: 'GET' });
  }

  async create(payload: SessionFormPayload): Promise<FetchResponseI<SessionI>> {
    return fetchData<SessionI>(this.endpoint, { method: 'POST', data: payload });
  }

  async update(id: string, payload: SessionFormPayload): Promise<FetchResponseI<SessionI>> {
    return fetchData<SessionI>(`${this.endpoint}/${id}`, { method: 'PATCH', data: payload });
  }

  async softDelete(id: string): Promise<FetchResponseI<boolean>> {
    return fetchData<boolean>(`${this.endpoint}/${id}`, { method: 'DELETE' });
  }

  async markComplete(id: string): Promise<FetchResponseI<SessionI>> {
    return fetchData<SessionI>(`${this.endpoint}/${id}/complete`, { method: 'POST' });
  }

  async unmarkComplete(id: string): Promise<FetchResponseI<SessionI>> {
    return fetchData<SessionI>(`${this.endpoint}/${id}/uncomplete`, { method: 'POST' });
  }

  /** Per-session attendance KPIs (counts + percentage). */
  async getSessionStats(sessionId: string): Promise<FetchResponseI<SessionStatsI>> {
    return fetchData<SessionStatsI>(`${this.endpoint}/${sessionId}/stats`, { method: 'GET' });
  }

  // ── Attendances (8–13) ─────────────────────────────────────────────────────

  async listAttendances(
    sessionId: string,
    filters: AttendanceListFiltersI = {}
  ): Promise<FetchResponseI<SessionAttendanceI[]>> {
    return fetchData<SessionAttendanceI[]>(`${this.endpoint}/${sessionId}/attendances`, {
      method: 'GET',
      params: filters,
      includePagination: true,
    });
  }

  async addAttendee(
    sessionId: string,
    userId: string
  ): Promise<FetchResponseI<SessionAttendanceI>> {
    return fetchData<SessionAttendanceI>(`${this.endpoint}/${sessionId}/attendances`, {
      method: 'POST',
      data: { userId },
    });
  }

  async addAttendeesBulk(
    sessionId: string,
    userIds: string[]
  ): Promise<FetchResponseI<{ added: number; skippedExisting: number; total: number }>> {
    return fetchData(`${this.endpoint}/${sessionId}/attendances`, {
      method: 'POST',
      data: { userIds },
    });
  }

  /**
   * Bulk-add every employee currently enrolled in the parent course as attendee
   * of this session. Backend handles dedupe (existing rows are skipped, status
   * and comments are preserved). Idempotent.
   *
   * Same URL as `addAttendee`; backend dispatches by body shape:
   *   { userId }                         → single add (HTTP 201)
   *   { userIds: [...] }                 → bulk add (HTTP 200)
   *   { source: 'course-enrollment' }    → bulk sync (HTTP 200)
   */
  async addAllFromCourse(
    sessionId: string
  ): Promise<FetchResponseI<{ added: number; skippedExisting: number; totalEnrolled: number }>> {
    return fetchData(`${this.endpoint}/${sessionId}/attendances`, {
      method: 'POST',
      data: { source: 'course-enrollment' },
    });
  }

  async updateAttendance(
    sessionId: string,
    userId: string,
    payload: { status: AttendanceStatus; comment?: string | null }
  ): Promise<FetchResponseI<SessionAttendanceI>> {
    return fetchData<SessionAttendanceI>(
      `${this.endpoint}/${sessionId}/attendances/${userId}`,
      { method: 'PATCH', data: payload }
    );
  }

  async bulkAttendance(
    sessionId: string,
    payload: AttendanceBulkPayload
  ): Promise<FetchResponseI<SessionAttendanceI[]>> {
    return fetchData<SessionAttendanceI[]>(`${this.endpoint}/${sessionId}/attendances/bulk`, {
      method: 'PUT',
      data: payload,
    });
  }

  async removeAttendee(sessionId: string, userId: string): Promise<FetchResponseI<boolean>> {
    return fetchData<boolean>(`${this.endpoint}/${sessionId}/attendances/${userId}`, {
      method: 'DELETE',
    });
  }

  async exportAttendance(
    sessionId: string,
    filters: Pick<AttendanceListFiltersI, 'search' | 'status'> = {}
  ): Promise<FetchResponseI<Blob>> {
    return fetchData<Blob>(`${this.endpoint}/${sessionId}/attendances/export`, {
      method: 'GET',
      params: filters,
      responseType: 'blob',
    });
  }

  // ── Evidences (14–16) ──────────────────────────────────────────────────────

  async listEvidences(
    sessionId: string,
    filters: EvidencesListFiltersI = {}
  ): Promise<FetchResponseI<SessionEvidenceI[]>> {
    return fetchData<SessionEvidenceI[]>(`${this.endpoint}/${sessionId}/evidences`, {
      method: 'GET',
      params: filters,
      includePagination: true,
    });
  }

  async uploadEvidence(
    sessionId: string,
    file: File,
    onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
  ): Promise<FetchResponseI<SessionEvidenceI>> {
    const formData = new FormData();
    formData.append('file', file);
    return fetchData<SessionEvidenceI>(`${this.endpoint}/${sessionId}/evidences`, {
      method: 'POST',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
  }

  async deleteEvidence(sessionId: string, evidenceId: string): Promise<FetchResponseI<boolean>> {
    return fetchData<boolean>(`${this.endpoint}/${sessionId}/evidences/${evidenceId}`, {
      method: 'DELETE',
    });
  }
}

const sessionsApi = new SessionsApi();
export default sessionsApi;
